
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useUploadCatalogPage } from "@/hooks/useCatalogs";

interface UploadPageFormProps {
  catalogId: string;
}

export function UploadPageForm({ catalogId }: UploadPageFormProps) {
  // Move all hooks to the top level
  const uploadMutation = useUploadCatalogPage();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [pageNumber, setPageNumber] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [styleNumbers, setStyleNumbers] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file || !pageNumber) {
      toast({
        title: "Error",
        description: "Please provide both a file and page number",
        variant: "destructive",
      });
      return;
    }

    const parsedStyleNumbers = styleNumbers
      .split(",")
      .map(num => parseInt(num.trim()))
      .filter(num => !isNaN(num));

    uploadMutation.mutate(
      {
        catalogId,
        pageNumber: parseInt(pageNumber),
        pageTitle: pageTitle || undefined,
        styleNumbers: parsedStyleNumbers,
        file
      },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Page uploaded successfully",
          });
          setFile(null);
          setPageNumber("");
          setPageTitle("");
          setStyleNumbers("");
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: "Failed to upload page. Please try again.",
            variant: "destructive",
          });
          console.error("Upload error:", error);
        },
      }
    );
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="file">Page Image (JPG or PNG)</Label>
          <Input
            id="file"
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pageNumber">Page Number</Label>
          <Input
            id="pageNumber"
            type="number"
            value={pageNumber}
            onChange={(e) => setPageNumber(e.target.value)}
            placeholder="Enter page number"
            min="1"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pageTitle">Page Title (Optional)</Label>
          <Input
            id="pageTitle"
            type="text"
            value={pageTitle}
            onChange={(e) => setPageTitle(e.target.value)}
            placeholder="Enter page title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="styleNumbers">Style Numbers (comma-separated)</Label>
          <Input
            id="styleNumbers"
            type="text"
            value={styleNumbers}
            onChange={(e) => setStyleNumbers(e.target.value)}
            placeholder="e.g., 9790, 9791, 9792"
          />
        </div>

        <Button
          type="submit"
          disabled={uploadMutation.isPending || !file || !pageNumber}
          className="w-full"
        >
          {uploadMutation.isPending ? "Uploading..." : "Upload Page"}
        </Button>
      </form>
    </Card>
  );
}
